import React, { useState } from 'react';
import { Bug, Send } from 'lucide-react';
import { useToast } from './TechComponents';
import { captureContext } from '../utils/bugReportCapture';

/**
 * BugReportInlineForm — Formulaire compact intégré au HelpPanel.
 *
 * Au submit : capture le contexte technique (URL, route, console, network, état
 * applicatif, screenshot DOM via html2canvas) et POST multipart vers /api/bug-reports.
 * Affiche un toast success avec le numéro `Bug portail #N` retourné par le backend.
 *
 * Le composant porte `data-bug-capture-skip="true"` pour être exclu du screenshot
 * — html2canvas ignore cet élément et ses descendants.
 */
const CATEGORIES = [
  { value: 'rights',  label: 'Droits / Permissions' },
  { value: 'ui',      label: 'Affichage / UI' },
  { value: 'backend', label: 'Erreur backend / 500' },
  { value: 'perf',    label: 'Performance / lenteur' },
  { value: 'data',    label: 'Données incorrectes' },
  { value: 'other',   label: 'Autre' },
];

const MAX_TITLE = 200;
const MAX_DESCRIPTION = 10000;

export default function BugReportInlineForm({ onClose, onSubmitted }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;

    const t = title.trim();
    const d = description.trim();
    if (!t) {
      toast.warning('Donne un titre court au bug avant d\'envoyer.');
      return;
    }
    if (!d) {
      toast.warning('Décris ce que tu faisais et ce qui ne va pas avant d\'envoyer.');
      return;
    }

    setSubmitting(true);
    try {
      const ctx = await captureContext({ includeScreenshot });
      const formData = new FormData();
      formData.append('payload', JSON.stringify({
        title: t.slice(0, MAX_TITLE),
        description: d.slice(0, MAX_DESCRIPTION),
        category,
        capture: ctx.capture,
        console_log: ctx.console_log,
        network_failures: ctx.network_failures,
        app_state: ctx.app_state,
      }));
      if (ctx.screenshot_blob) {
        formData.append('screenshot', ctx.screenshot_blob, 'screenshot.png');
      }

      const res = await fetch('/api/bug-reports', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const errMsg = (data && (data.error || data.detail)) || `HTTP ${res.status}`;
        throw new Error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
      toast.success(`Bug portail #${data.bug_report.number} signalé. Merci !`);
      setTitle('');
      setDescription('');
      setCategory('other');
      if (typeof onSubmitted === 'function') onSubmitted(data.bug_report);
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      toast.error(`Échec du signalement : ${err.message || String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-bug-capture-skip="true"
      style={{
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 10, fontSize: 12, fontWeight: 700, color: '#b91c1c',
      }}>
        <Bug size={14} />
        <span>Signaler un bug</span>
      </div>

      <input
        type="text"
        placeholder="Titre court (max 200 caractères)"
        value={title}
        onChange={e => setTitle(e.target.value)}
        maxLength={MAX_TITLE}
        disabled={submitting}
        style={inputStyle}
      />

      <textarea
        placeholder="Que faisais-tu ? Que s'est-il passé ? Que devrait-il se passer ?"
        value={description}
        onChange={e => setDescription(e.target.value)}
        maxLength={MAX_DESCRIPTION}
        disabled={submitting}
        rows={5}
        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
      />

      <select
        value={category}
        onChange={e => setCategory(e.target.value)}
        disabled={submitting}
        style={inputStyle}
      >
        {CATEGORIES.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>

      <label style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, color: '#7f1d1d', marginBottom: 10, cursor: 'pointer',
      }}>
        <input
          type="checkbox"
          checked={includeScreenshot}
          onChange={e => setIncludeScreenshot(e.target.checked)}
          disabled={submitting}
        />
        Inclure une capture d'écran de la page
      </label>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            flex: 1,
            background: submitting ? '#fca5a5' : '#dc2626',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: submitting ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Send size={12} />
          {submitting ? 'Envoi…' : 'Envoyer'}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={{
            background: 'transparent',
            color: '#7f1d1d',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  marginBottom: 8,
  fontSize: 12,
  border: '1px solid #fecaca',
  borderRadius: 6,
  background: '#fff',
  color: 'var(--text)',
  fontFamily: "'IBM Plex Sans', sans-serif",
  boxSizing: 'border-box',
};
